import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, SortingState, useReactTable } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

type Props<T extends object> = {
  data: T[] | undefined
  columns: ColumnDef<T, any>[]
  loading?: boolean
  empty?: string
}

export function DataTable<T extends object>({ data, columns, loading, empty = 'No data' }: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const table = useReactTable<T>({
    data: data ?? [],
    columns: useMemo(() => columns, [columns]),
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  })

  return (
    <div className="overflow-x-auto border rounded">
      <table className="w-full text-sm">
        <thead className="bg-secondary">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th key={h.id} className="text-left font-medium px-3 py-2 select-none">
                  {h.isPlaceholder ? null : (
                    <div
                      className={h.column.getCanSort() ? 'cursor-pointer' : ''}
                      onClick={h.column.getToggleSortingHandler()}
                    >
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {{ asc: ' ▲', desc: ' ▼' }[h.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading
            ? SkeletonRows(table.getAllColumns().length)
            : table.getRowModel().rows.length > 0
            ? table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-t">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            : (
                <tr>
                  <td colSpan={table.getAllColumns().length} className="px-3 py-6 text-center text-muted">
                    {empty}
                  </td>
                </tr>
              )}
        </tbody>
      </table>
    </div>
  )
}

function SkeletonRows(cols: number) {
  return Array.from({ length: 5 }).map((_, i) => (
    <tr key={`sk_${i}`} className="border-t">
      {Array.from({ length: cols }).map((__, j) => (
        <td key={`sk_${i}_${j}`} className="px-3 py-2">
          <div className="h-3 w-24 bg-secondary animate-pulse rounded" />
        </td>
      ))}
    </tr>
  ))
}

